import React, { useState, useEffect } from "react";
import { Form } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { verifyPhone } from "../../../slices/auth/register/thunk";
import { resetRegisterState } from "../../../slices/auth/register/reducer";
import { showSuccessToast, showErrorToast } from "../../../helpers/toast_helper";
import appConfig from "../../../config";
import withRouter from "../../../Components/Common/withRouter";
import DashboardHeader from "../../../Components/Common/DashboardHeader";
import AuthLeftPanel from "../../../Components/Common/AuthLeftPanel";
import MobileInput from "../../../Components/Common/MobileInput";
import AuthFormButton from "../../../Components/Common/AuthFormButton";
import AuthLink from "../../../Components/Common/AuthLink";

const RegisterPhone = () => {
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const [loader, setLoader] = useState<boolean>(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

  const [phoneData, setPhoneData] = useState({
    phoneno: '',
    dialcode: appConfig.country.defaultDialCode,
    countrycode: appConfig.country.defaultCountryCode,
  });

  const selectRegisterState = (state: any) => state.Register;
  const selectRegisterData = createSelector(selectRegisterState, (register) => ({
    loading: register.loading,
    successMsg: register.successMsg,
    errorMsg: register.errorMsg,
    phoneVerified: register.phoneVerified,
  }));

  const { loading, successMsg, errorMsg, phoneVerified } = useSelector(selectRegisterData);

  useEffect(() => {
    if (successMsg && phoneVerified) {
      showSuccessToast(successMsg, "phone-verification-success");
      setLoader(false);
      navigate("/register-otp");
      dispatch(resetRegisterState());
    }
  }, [successMsg, phoneVerified, navigate, dispatch]);

  useEffect(() => {
    if (errorMsg) {
      showErrorToast(errorMsg, "phone-verification-error");
      setLoader(false);
      dispatch(resetRegisterState());
    }
  }, [errorMsg, dispatch]);

  const mobileValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      phoneno: phoneData.phoneno || '',
      countrycode: phoneData.countrycode || appConfig.country.defaultCountryCode,
      dialcode: phoneData.dialcode || appConfig.country.defaultDialCode,
      languageid: 1,
    },
    validationSchema: Yup.object({
      phoneno: Yup.string()
        .required("Please enter mobile number")
        .matches(/^[0-9]{6,10}$/, "Please enter a valid mobile number"),
      countrycode: Yup.string().required("Country code is required"),
      dialcode: Yup.string().required("Dial code is required"),
    }),
    onSubmit: async (values) => {
      setLoader(true);
      try {
        setPhoneData({
          phoneno: values.phoneno,
          dialcode: values.dialcode,
          countrycode: values.countrycode,
        });
        const result = await dispatch(verifyPhone({
          phoneno: values.phoneno,
          countrycode: values.countrycode,
          dialcode: values.dialcode,
          languageid: values.languageid,
        }));
        if (result.success) {
          sessionStorage.setItem("phoneNumber", values.phoneno);
          sessionStorage.setItem("dialcode", values.dialcode);
          sessionStorage.setItem("countrycode", values.countrycode);
        } else {
          setLoader(false);
        }
      } catch (error: any) {
        console.error("Phone verification error:", error);
        setLoader(false);
      }
    },
  });

  document.title = "AMANAT - Register";

  return (
    <div className="auth-container">
      <DashboardHeader />
      <div className="auth-main-content">
        <div className="auth-panel-container">
          <AuthLeftPanel
            title="Register"
            description="Welcome to AMANAT. Register now to experience a safer, smarter way forward."
          />
          <div className="auth-right-panel">
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                if (!termsAccepted) return;
                mobileValidation.handleSubmit();
                return false;
              }}
              className="auth-form"
            >
              <div className="auth-form-group">
                <label htmlFor="phoneno" className="auth-form-label">
                  Mobile number
                </label>
                <MobileInput
                  name="phoneno"
                  value={mobileValidation.values.phoneno}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    mobileValidation.setFieldValue('phoneno', value);
                  }}
                  onBlur={mobileValidation.handleBlur}
                  dialcode={mobileValidation.values.dialcode}
                  onDialCodeChange={(dialcode, countrycode) => {
                    mobileValidation.setFieldValue('dialcode', dialcode);
                    mobileValidation.setFieldValue('countrycode', countrycode);
                  }}
                  error={mobileValidation.errors.phoneno}
                  touched={mobileValidation.touched.phoneno}
                  placeholder="Enter your mobile number"
                />
              </div>
              <div className="auth-legal-checkbox">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <label htmlFor="termsAccepted" className="auth-legal-text">
                  <Link
                    to="/terms-and-conditions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms &amp; Conditions
                  </Link>
                  {" "}and{" "}
                  <Link
                    to="/privacy-policy"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </Link>
                  .
                </label>
              </div>
              <div className="auth-form-group">
                <AuthFormButton
                  loading={loader || loading}
                  disabled={!termsAccepted}
                  type="submit"
                >
                  Request Otp
                </AuthFormButton>
              </div>
            </Form>
            <AuthLink to="/login" text="Already have an account?" linkText="Login here" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(RegisterPhone);

